import React, { useCallback, useEffect, useState, useRef } from 'react';
import { fetchBooks, fetchAllOrders, updateOrderStatus, deleteBook } from '../api';
import { useNavigate } from 'react-router-dom';
import QRCode from "react-qr-code";
import html2canvas from 'html2canvas';
import AdminCouponManager from "../components/AdminCouponManager";
import AdminHomepageSectionManager from "../components/AdminHomepageSectionManager";
import AdminBannerManager from "../components/AdminBannerManager";
import AdminAuthorManager from "../components/AdminAuthorManager";
import AdminCategoryManager from "../components/AdminCategoryManager";
import AdminDealerManager from "../components/AdminDealerManager";
import AdminMailSubscriberManager from "../components/AdminMailSubscriberManager";

const AdminPanel = ({ user }) => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('orders'); // 'orders' | 'books' | 'qr' | 'homepage' | 'coupons'
    const [orderSubTab, setOrderSubTab] = useState('pending'); // 'pending' or 'delivered'
    const [books, setBooks] = useState([]);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [trackingInputs, setTrackingInputs] = useState({});
    const [postOfficeInputs, setPostOfficeInputs] = useState({});
    const [selectedBookId, setSelectedBookId] = useState("");
    const [bookSearch, setBookSearch] = useState("");
    const [deletingBookId, setDeletingBookId] = useState(null);
    const [detailOrderId, setDetailOrderId] = useState(null);
    const qrCodeRef = useRef(null);
    const tabsRef = useRef(null);

    const handleTabsWheel = (e) => {
        if (tabsRef.current) {
            if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
                e.preventDefault();
                tabsRef.current.scrollLeft += e.deltaY;
            }
        }
    };

    const scrollTabs = (direction) => {
        if (tabsRef.current) {
            tabsRef.current.scrollBy({ left: direction * 200, behavior: 'smooth' });
        }
    };

    const loadBooks = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        setError(null);
        try {
            const data = await fetchBooks();
            setBooks(data);
        } catch (err) {
            setError(err.message);
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    const loadOrders = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        setError(null);
        try {
            const data = await fetchAllOrders(user.token);
            setOrders(data);
        } catch (err) {
            setError(err.message);
        } finally {
            if (!silent) setLoading(false);
        }
    }, [user.token]);

    useEffect(() => {
        // Initial data fetch on mount
        const loadInitialData = async () => {
            setLoading(true);
            try {
                await Promise.all([loadBooks(true), loadOrders(true)]);
            } catch (err) {
                console.error("Initial load error:", err);
            } finally {
                setLoading(false);
            }
        };
        loadInitialData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Empty dependency array means it only runs on mount

    const handleTrackingInputChange = (orderId, value) => {
        setTrackingInputs(prev => ({
            ...prev,
            [orderId]: value
        }));
    };

    const handlePostOfficeInputChange = (orderId, value) => {
        setPostOfficeInputs(prev => ({
            ...prev,
            [orderId]: value
        }));
    };

    const handleUpdateStatus = async (orderId, newStatus) => {
        try {
            const trackingId = trackingInputs[orderId] || undefined;
            const postOffice = postOfficeInputs[orderId] || undefined;
            await updateOrderStatus(user.token, orderId, {
                deliveryStatus: newStatus,
                trackingId,
                postOffice
            });
            loadOrders();
        } catch (err) {
            alert(err.message || 'Failed to update order status');
        }
    };

    const handleDeleteBook = async (id) => {
        try {
            await deleteBook(user.token, id);
            loadBooks();
            alert("Book deleted successfully!");
        } catch (err) {
            alert(err.message || 'Failed to delete book');
        } finally {
            setDeletingBookId(null);
        }
    };

    const shorten = (str, len = 45) => (str && str.length > len) ? str.substring(0, len) + "..." : (str || "");

    // Filter orders based on sub-tab
    const pendingOrders = orders.filter(o => o.deliveryStatus !== 'Delivered');
    const deliveredOrders = orders.filter(o => o.deliveryStatus === 'Delivered');
    const filteredOrders = orderSubTab === 'pending' ? pendingOrders : deliveredOrders;

    const filteredBooks = books.filter(b =>
        (b.title && b.title.toLowerCase().includes(bookSearch.toLowerCase())) ||
        (b.author && b.author.toLowerCase().includes(bookSearch.toLowerCase()))
    );

    const selectedBook = books.find(b => String(b._id) === String(selectedBookId)) || null;
    const selectedBookUrl = selectedBook ? `${window.location.origin}/book/${selectedBook._id}` : "";
    // Use the site's production URL for the QR code unless we're on localhost
    const QrUrl = selectedBook
        ? (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
            ? selectedBookUrl
            : `https://bharatlawhouse.com/book/${selectedBook._id}`)
        : "";

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            alert("Copied to clipboard");
        } catch {
            const ta = document.createElement("textarea");
            ta.value = text;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand("copy");
            document.body.removeChild(ta);
            alert("Copied to clipboard");
        }
    };

    const downloadQRCode = async () => {
        if (!qrCodeRef.current || !selectedBook) return;

        try {
            const canvas = await html2canvas(qrCodeRef.current, {
                backgroundColor: '#ffffff',
                scale: 2,
            });

            const link = document.createElement('a');
            link.href = canvas.toDataURL('image/png');
            link.download = `${selectedBook.title.replace(/\s+/g, '_')}_QR.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            alert('Failed to download QR code: ' + (err.message || 'Unknown error'));
        }
    };

    return (
        <div className="admin-panel-section" style={{ minHeight: "60vh", padding: "40px 20px" }}>
            <style>{`
                .admin-tabs-wrapper {
                    position: relative;
                    margin-bottom: 30px;
                    display: flex;
                    align-items: center;
                }
                .admin-tabs {
                    display: flex;
                    flex-wrap: nowrap;
                    gap: 8px;
                    overflow-x: auto;
                    -webkit-overflow-scrolling: touch;
                    scrollbar-width: thin;
                    scrollbar-color: var(--forest) rgba(0,0,0,0.06);
                    background: rgba(0,0,0,0.03);
                    padding: 8px 10px;
                    border-radius: 12px;
                    border: 1px solid var(--border);
                    width: 100%;
                    scroll-behavior: smooth;
                }
                .admin-tabs::-webkit-scrollbar {
                    height: 6px;
                }
                .admin-tabs::-webkit-scrollbar-track {
                    background: rgba(0,0,0,0.04);
                    border-radius: 4px;
                }
                .admin-tabs::-webkit-scrollbar-thumb {
                    background: var(--forest);
                    border-radius: 4px;
                }
                .admin-tabs::-webkit-scrollbar-thumb:hover {
                    background: #254a35;
                }
                .tab-scroll-btn {
                    background: white;
                    border: 1px solid var(--border);
                    border-radius: 50%;
                    width: 32px;
                    height: 32px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    box-shadow: 0 2px 6px rgba(0,0,0,0.08);
                    position: absolute;
                    z-index: 10;
                    color: var(--forest);
                    font-size: 14px;
                    font-weight: 700;
                    transition: all 0.2s ease;
                }
                .tab-scroll-btn:hover {
                    background: var(--forest);
                    color: white;
                    border-color: var(--forest);
                }
                .tab-scroll-btn.left {
                    left: -14px;
                }
                .tab-scroll-btn.right {
                    right: -14px;
                }
                .tab-btn {
                    background: transparent;
                    border: none;
                    padding: 10px 20px;
                    cursor: pointer;
                    font-size: 0.95rem;
                    font-weight: 600;
                    white-space: nowrap;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    border-radius: 8px;
                    flex-shrink: 0;
                }
                .tab-btn:hover:not(.active) {
                    background: rgba(0,0,0,0.04);
                }
                .tab-btn.active {
                    background: white !important;
                    color: var(--forest) !important;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04);
                }
                .admin-header { margin-bottom: 30px; }
                
                @media (max-width: 991px) {
                    .admin-panel-section { padding: 0 16px 24px !important; }
                    .admin-header { padding: 24px 0 0; }
                    .admin-header h1 { font-size: 1.8rem !important; }
                    .admin-tabs-wrapper { 
                        position: sticky; 
                        top: 70px; 
                        background: white; 
                        z-index: 100; 
                        margin: 0 -16px 24px;
                        padding: 6px 16px;
                    }
                    .admin-tabs {
                        padding: 6px 4px;
                        border-radius: 8px;
                    }
                    .tab-scroll-btn {
                        display: none;
                    }
                    .tab-btn { font-size: 0.95rem !important; padding: 10px 14px !important; }
                    .order-card-flex { flex-direction: column !important; gap: 16px !important; padding: 16px !important; }
                    .order-items-col { border-left: none !important; border-top: 1px solid var(--border) !important; padding-left: 0 !important; padding-top: 16px !important; }
                    .book-grid { grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)) !important; gap: 12px !important; }
                    .qr-grid { grid-template-columns: 1fr !important; }
                }
                @media (max-width: 576px) {
                    .order-subtab-btns { flex-direction: column !important; gap: 8px !important; }
                    .order-subtab-btns button { width: 100% !important; border-radius: 8px !important; }
                }

                .admin-loader {
                    width: 48px;
                    height: 48px;
                    border: 4px solid #f3f3f3;
                    border-top: 4px solid var(--forest);
                    border-radius: 50%;
                    margin: 0 auto;
                    animation: spin 1s linear infinite;
                }

                .delete-modal-overlay {
                    position: fixed;
                    top: 0; left: 0; right: 0; bottom: 0;
                    background: rgba(0,0,0,0.45);
                    z-index: 9999;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 20px;
                    animation: fadeIn 0.2s ease;
                }
                .delete-modal {
                    background: white;
                    border-radius: 16px;
                    padding: 28px 24px 24px;
                    max-width: 400px;
                    width: 100%;
                    box-shadow: 0 20px 60px rgba(0,0,0,0.2);
                    animation: slideUp 0.25s ease;
                    text-align: center;
                }
                .delete-modal-icon {
                    width: 56px; height: 56px;
                    border-radius: 50%;
                    background: #fff3f3;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 16px;
                    font-size: 1.6rem;
                }
                .delete-modal h3 {
                    margin: 0 0 8px; font-size: 1.15rem; color: #1a1a1a;
                }
                .delete-modal p {
                    margin: 0 0 6px; font-size: 0.88rem; color: #666; line-height: 1.5;
                }
                .delete-modal .book-title-highlight {
                    font-weight: 700; color: #333; background: #f8f8f8;
                    padding: 2px 8px; border-radius: 4px;
                }
                .delete-modal-actions {
                    display: flex; gap: 10px; margin-top: 20px;
                }
                .delete-modal-actions button {
                    flex: 1; padding: 10px 16px; border-radius: 10px;
                    font-size: 0.9rem; font-weight: 600; cursor: pointer;
                    transition: all 0.15s ease; border: none;
                }
                .delete-modal-cancel {
                    background: #f0f0f0; color: #333;
                }
                .delete-modal-cancel:hover { background: #e0e0e0; }
                .delete-modal-confirm {
                    background: #dc3545; color: white;
                }
                .delete-modal-confirm:hover { background: #c82333; }
                @keyframes fadeIn {
                    from { opacity: 0; } to { opacity: 1; }
                }
                @keyframes slideUp {
                    from { opacity: 0; transform: translateY(16px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
            <div style={{ maxWidth: "1000px", margin: "0 auto" }}>
                <div className="admin-header">
                    <h1 style={{ color: "var(--forest)", marginBottom: "10px" }}>Admin Dashboard</h1>
                    <p style={{ color: "var(--text-light)", marginBottom: "0" }}>
                        Welcome back, {user?.name}. Manage your site here.
                    </p>
                </div>

                {/* Tabs with scroll navigation */}
                <div className="admin-tabs-wrapper">
                    <button
                        className="tab-scroll-btn left"
                        onClick={() => scrollTabs(-1)}
                        aria-label="Scroll tabs left"
                    >
                        ❮
                    </button>
                    <div 
                        className="admin-tabs" 
                        ref={tabsRef}
                        onWheel={handleTabsWheel}
                    >
                        <button
                            onClick={() => setActiveTab('orders')}
                            className={`tab-btn ${activeTab === 'orders' ? 'active' : ''}`}
                            style={{ color: activeTab === 'orders' ? 'var(--forest)' : 'var(--text-light)' }}
                        >
                            Manage Orders
                        </button>
                        <button
                            onClick={() => setActiveTab('books')}
                            className={`tab-btn ${activeTab === 'books' ? 'active' : ''}`}
                            style={{ color: activeTab === 'books' ? 'var(--forest)' : 'var(--text-light)' }}
                        >
                            View Books
                        </button>
                        <button
                            onClick={() => setActiveTab('qr')}
                            className={`tab-btn ${activeTab === 'qr' ? 'active' : ''}`}
                            style={{ color: activeTab === 'qr' ? 'var(--forest)' : 'var(--text-light)' }}
                        >
                            QR Codes
                        </button>
                        <button
                            onClick={() => setActiveTab('homepage')}
                            className={`tab-btn ${activeTab === 'homepage' ? 'active' : ''}`}
                            style={{ color: activeTab === 'homepage' ? 'var(--forest)' : 'var(--text-light)' }}
                        >
                            Homepage
                        </button>
                        <button
                            onClick={() => setActiveTab('authors')}
                            className={`tab-btn ${activeTab === 'authors' ? 'active' : ''}`}
                            style={{ color: activeTab === 'authors' ? 'var(--forest)' : 'var(--text-light)' }}
                        >
                            Authors
                        </button>
                        <button
                            onClick={() => setActiveTab('coupons')}
                            className={`tab-btn ${activeTab === 'coupons' ? 'active' : ''}`}
                            style={{ color: activeTab === 'coupons' ? 'var(--forest)' : 'var(--text-light)' }}
                        >
                            Coupon System
                        </button>
                        <button
                            onClick={() => setActiveTab('categories')}
                            className={`tab-btn ${activeTab === 'categories' ? 'active' : ''}`}
                            style={{ color: activeTab === 'categories' ? 'var(--forest)' : 'var(--text-light)' }}
                        >
                            Categories
                        </button>
                        <button
                            onClick={() => setActiveTab('dealers')}
                            className={`tab-btn ${activeTab === 'dealers' ? 'active' : ''}`}
                            style={{ color: activeTab === 'dealers' ? 'var(--forest)' : 'var(--text-light)' }}
                        >
                            Dealers
                        </button>
                        <button
                            onClick={() => setActiveTab('mail-subscribers')}
                            className={`tab-btn ${activeTab === 'mail-subscribers' ? 'active' : ''}`}
                            style={{ color: activeTab === 'mail-subscribers' ? 'var(--forest)' : 'var(--text-light)' }}
                        >
                            Mail Subscribers
                        </button>
                    </div>
                    <button
                        className="tab-scroll-btn right"
                        onClick={() => scrollTabs(1)}
                        aria-label="Scroll tabs right"
                    >
                        ❯
                    </button>
                </div>

                {loading ? (
                    <div style={{ textAlign: "center", padding: "80px 20px" }}>
                        <div className="admin-loader"></div>
                        <p style={{ color: "var(--text-light)", marginTop: "16px", fontWeight: 500 }}>
                            Preparing your dashboard...
                        </p>
                    </div>
                ) : error ? (
                    <div style={{ color: 'red', textAlign: 'center', padding: '20px' }}>{error}</div>
                ) : (
                    <>
                        {/* Books Tab Content */}
                        {activeTab === 'books' && (
                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', alignItems: 'center', flexWrap: 'wrap', gap: '20px' }}>
                                    <input
                                        type="text"
                                        placeholder="Search books..."
                                        value={bookSearch}
                                        onChange={(e) => setBookSearch(e.target.value)}
                                        style={{
                                            flex: '1 1 200px',
                                            maxWidth: '500px',
                                            padding: '10px 16px',
                                            borderRadius: '8px',
                                            border: '1px solid var(--border)',
                                            fontFamily: 'inherit',
                                            fontSize: '0.95rem'
                                        }}
                                    />
                                    <button className="btn btn-forest" onClick={() => navigate('/admin/upload')}>
                                        + Upload New
                                    </button>
                                </div>
                                {(() => {
                                    const brokenCount = filteredBooks.filter(b => b.hasBrokenImage || b.hasBrokenPdf).length;
                                    return brokenCount > 0 ? (
                                        <div style={{
                                            background: '#fff3f3', border: '1px solid #f5c6cb', borderRadius: '10px',
                                            padding: '12px 16px', marginBottom: '20px', color: '#721c24',
                                            fontSize: '0.9rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '10px',
                                        }}>
                                            <span style={{ fontSize: '1.3rem' }}>⚠️</span>
                                            <span>
                                                <strong>{brokenCount} book{brokenCount > 1 ? 's' : ''}</strong> {brokenCount > 1 ? 'have' : 'has'} images/PDFs
                                                that were on Vercel Blob and are no longer accessible.
                                                Click <strong>"⚠ Fix"</strong> on each to re-upload.
                                            </span>
                                        </div>
                                    ) : null;
                                })()}
                                <div className="book-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '20px' }}>
                                    {filteredBooks.length === 0 ? <p>No books found.</p> : filteredBooks.map(book => (
                                        <div key={book._id} style={{
                                            background: 'var(--paper)', borderRadius: 'var(--r)', padding: '15px',
                                            boxShadow: '0 2px 10px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column',
                                            border: (book.hasBrokenImage || book.hasBrokenPdf) ? '2px solid #dc3545' : 'none',
                                        }}>
                                            <div style={{
                                                width: '100%', height: '180px', backgroundColor: '#f0f0f0',
                                                borderRadius: 'var(--r)', marginBottom: '10px', position: 'relative',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                overflow: 'hidden'
                                            }}>
                                                {!book.hasBrokenImage && (book.coverImage || book.img) && (
                                                    <img src={book.coverImage || book.img} alt={book.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                )}
                                                {book.hasBrokenImage && (
                                                    <div style={{
                                                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
                                                        color: '#dc3545', textAlign: 'center', padding: '10px',
                                                        position: 'absolute'
                                                    }}>
                                                        <span style={{ fontSize: '2rem' }}>⚠️</span>
                                                        <span style={{ fontSize: '0.75rem', fontWeight: 700, lineHeight: 1.3 }}>Image needs<br/>re-upload</span>
                                                    </div>
                                                )}
                                            </div>
                                            {(book.hasBrokenImage || book.hasBrokenPdf) && (
                                                <div style={{
                                                    background: '#fff3f3', border: '1px solid #f5c6cb', borderRadius: '6px',
                                                    padding: '6px 10px', marginBottom: '8px', fontSize: '0.7rem', color: '#721c24',
                                                    fontWeight: 600, lineHeight: 1.4,
                                                }}>
                                                    {book.hasBrokenImage && '📷 Cover image lost. '}
                                                    {book.hasBrokenPdf && '📄 Preview PDF lost. '}
                                                    Click Edit to re-upload.
                                                </div>
                                            )}
                                            <h3 style={{ fontSize: '1rem', margin: '0 0 5px 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{book.title}</h3>
                                            <p style={{ color: 'var(--text-light)', fontSize: '0.85rem', margin: '0 0 10px 0' }}>{book.author}</p>
                                            <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                                                <strong style={{ fontSize: '0.95rem' }}>₹{(book.rawPrice || book.price || 0).toFixed(0)}</strong>
                                                <div style={{ display: 'flex', gap: '6px' }}>
                                                    <button
                                                        onClick={() => navigate(`/admin/update/${book._id}`)}
                                                        style={{
                                                            border: 'none',
                                                            background: (book.hasBrokenImage || book.hasBrokenPdf) ? '#dc3545' : '#C8960A',
                                                            color: 'white', borderRadius: '4px', padding: '4px 8px',
                                                            cursor: 'pointer', fontSize: '0.75rem',
                                                            fontWeight: (book.hasBrokenImage || book.hasBrokenPdf) ? 700 : 400,
                                                        }}>
                                                        {(book.hasBrokenImage || book.hasBrokenPdf) ? '⚠ Fix' : 'Edit'}
                                                    </button>
                                                    <button
                                                        onClick={() => setDeletingBookId(book._id)}
                                                        style={{ border: 'none', background: '#dc3545', color: 'white', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontSize: '0.75rem' }}>
                                                        Del
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* QR Tab Content */}
                        {activeTab === 'qr' && (
                            <div className="qr-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 24 }}>
                                <div style={{ background: "var(--paper)", border: "1px solid var(--border)", borderRadius: 12, padding: 18 }}>
                                    <h3 style={{ marginTop: 0, marginBottom: 16 }}>QR Generator</h3>
                                    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                                        <select
                                            value={selectedBookId}
                                            onChange={(e) => setSelectedBookId(e.target.value)}
                                            style={{
                                                padding: "12px",
                                                borderRadius: 10,
                                                border: "1px solid var(--border)",
                                                background: "white",
                                                fontFamily: "inherit",
                                                fontSize: "0.9rem",
                                                width: "100%"
                                            }}
                                        >
                                            <option value="">Select a book…</option>
                                            {books.map(b => (
                                                <option key={b._id} value={b._id}>
                                                    {shorten(b.title, 40)}
                                                </option>
                                            ))}
                                        </select>

                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                                            <button
                                                className="btn btn-primary"
                                                style={{ padding: "10px" }}
                                                onClick={() => selectedBookUrl && window.open(selectedBookUrl, "_blank")}
                                                disabled={!selectedBookUrl}
                                            >
                                                Open
                                            </button>
                                            <button
                                                className="btn btn-primary"
                                                style={{ padding: "10px" }}
                                                onClick={() => selectedBookUrl && copyToClipboard(selectedBookUrl)}
                                                disabled={!selectedBookUrl}
                                            >
                                                Copy URL
                                            </button>
                                        </div>
                                        <button
                                            className="btn btn-forest"
                                            style={{ padding: "12px", width: "100%" }}
                                            onClick={downloadQRCode}
                                            disabled={!selectedBookUrl}
                                        >
                                            ↓ Download QR Image
                                        </button>
                                    </div>

                                    {selectedBook && (
                                        <div style={{ marginTop: 20, padding: 12, background: '#f8f9fa', borderRadius: 8, fontSize: 12 }}>
                                            <div style={{ marginBottom: 4 }}><strong>ID:</strong> {selectedBook._id}</div>
                                            <div style={{ wordBreak: "break-all" }}><strong>URL:</strong> {selectedBookUrl}</div>
                                        </div>
                                    )}
                                </div>

                                <div style={{ background: "var(--paper)", border: "1px solid var(--border)", borderRadius: 12, padding: 24, display: "flex", flexDirection: "column", alignItems: "center" }}>
                                    {selectedBook ? (
                                        <div ref={qrCodeRef} style={{
                                            background: "white", padding: "24px", borderRadius: 16,
                                            border: "1px solid var(--border)", display: "flex", flexDirection: "column",
                                            alignItems: "center", width: "100%", maxWidth: "300px",
                                            boxShadow: "0 10px 25px rgba(0,0,0,0.05)"
                                        }}>
                                            <div style={{ marginBottom: 16, textAlign: "center" }}>
                                                <h3 style={{ margin: "0 0 2px 0", color: "var(--forest)", fontSize: "1.1rem", fontWeight: 800 }}>{shorten(selectedBook.title, 35)}</h3>
                                                <p style={{ margin: 0, color: "var(--text-light)", fontSize: "0.85rem" }}>{shorten(selectedBook.author, 25)}</p>
                                            </div>
                                            <div style={{ padding: 12, borderRadius: 12, border: "1px solid #f0f0f0", marginBottom: 16 }}>
                                                <QRCode value={QrUrl} size={180} style={{ height: "auto", maxWidth: "100%", width: "100%" }} />
                                            </div>
                                            <div style={{ textAlign: "center" }}>
                                                <div style={{ fontSize: "1rem", fontWeight: 800, color: "var(--forest)" }}>₹{(selectedBook.rawPrice || selectedBook.price || 0).toFixed(0)}</div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div style={{ color: "var(--text-light)", fontSize: 14, textAlign: "center", padding: "60px 20px" }}>
                                            Select a book to generate QR
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {activeTab === 'homepage' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
                                <AdminBannerManager user={user} />
                                <div style={{ borderTop: '2px solid var(--border)', paddingTop: 32 }}>
                                    <AdminHomepageSectionManager user={user} books={books} />
                                </div>
                            </div>
                        )}

                        {activeTab === 'authors' && (
                            <AdminAuthorManager user={user} />
                        )}

                        {activeTab === 'categories' && (
                            <AdminCategoryManager user={user} />
                        )}

                        {activeTab === 'dealers' && (
                            <AdminDealerManager user={user} />
                        )}

                        {activeTab === 'mail-subscribers' && (
                            <AdminMailSubscriberManager user={user} />
                        )}

                        {activeTab === 'coupons' && (
                            <AdminCouponManager user={user} books={books} />
                        )}

                        {/* Orders Tab Content */}
                        {activeTab === 'orders' && (
                            <div>
                                {/* Order Sub-Tabs */}
                                <div className="order-subtab-btns" style={{ display: 'flex', gap: '10px', marginBottom: '24px' }}>
                                    <button
                                        onClick={() => setOrderSubTab('pending')}
                                        style={{
                                            flex: 1, padding: '10px 20px', borderRadius: '12px', cursor: 'pointer',
                                            fontSize: '0.9rem', fontWeight: '600',
                                            border: orderSubTab === 'pending' ? '2px solid var(--forest)' : '2px solid var(--border)',
                                            background: orderSubTab === 'pending' ? 'rgba(57,109,79,0.05)' : 'white',
                                            color: orderSubTab === 'pending' ? 'var(--forest)' : 'var(--text-light)',
                                        }}
                                    >
                                        Pending ({pendingOrders.length})
                                    </button>
                                    <button
                                        onClick={() => setOrderSubTab('delivered')}
                                        style={{
                                            flex: 1, padding: '10px 20px', borderRadius: '12px', cursor: 'pointer',
                                            fontSize: '0.9rem', fontWeight: '600',
                                            border: orderSubTab === 'delivered' ? '2px solid #28a745' : '2px solid var(--border)',
                                            background: orderSubTab === 'delivered' ? 'rgba(40,167,69,0.05)' : 'white',
                                            color: orderSubTab === 'delivered' ? '#28a745' : 'var(--text-light)',
                                        }}
                                    >
                                        Delivered ({deliveredOrders.length})
                                    </button>
                                </div>

                                {/* Filtered Order Cards */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                    {filteredOrders.length === 0 ? (
                                        <p style={{ textAlign: 'center', color: 'var(--text-light)', padding: '60px 0' }}>
                                            No orders in this category.
                                        </p>
                                    ) : filteredOrders.map(order => {
                                        const orderId = String(order._id || order.id || "");
                                        const addr = order.shippingAddress || {};
                                        return (
                                            <div key={orderId} className="order-card-flex" style={{
                                                background: 'white', borderRadius: '16px', padding: '24px',
                                                border: '1px solid var(--border)', display: 'flex', gap: '24px'
                                            }}>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                                                        <div>
                                                            <h3 style={{ margin: '0 0 4px 0', fontSize: '1.1rem' }}>Order #{orderId.slice(-6)}</h3>
                                                            <span style={{
                                                                padding: '4px 10px', borderRadius: '8px', fontSize: '0.75rem', fontWeight: '700',
                                                                backgroundColor: order.deliveryStatus === 'Delivered' ? '#e6f4ea' : '#fff8e1',
                                                                color: order.deliveryStatus === 'Delivered' ? '#1e7e34' : '#f57f17',
                                                            }}>
                                                                {order.deliveryStatus || 'Pending'}
                                                            </span>
                                                        </div>
                                                        <button
                                                            onClick={() => setDetailOrderId(orderId)}
                                                            style={{
                                                                border: '1px solid var(--border)', background: 'white', color: 'var(--text-light)',
                                                                borderRadius: '8px', padding: '4px 12px', cursor: 'pointer', fontSize: '0.8rem',
                                                                fontWeight: '600', transition: 'all 0.15s ease', whiteSpace: 'nowrap'
                                                            }}
                                                            onMouseEnter={e => { e.target.style.background = 'var(--forest)'; e.target.style.color = 'white'; e.target.style.borderColor = 'var(--forest)'; }}
                                                            onMouseLeave={e => { e.target.style.background = 'white'; e.target.style.color = 'var(--text-light)'; e.target.style.borderColor = 'var(--border)'; }}
                                                        >
                                                            Details
                                                        </button>
                                                    </div>

                                                    {/* Dates Section */}
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '16px', fontSize: '0.82rem' }}>
                                                        <div style={{ background: '#f0f7ff', padding: '6px 12px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                            <span>📅</span>
                                                            <span><strong>Received:</strong> {order.createdAt ? new Date(order.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</span>
                                                        </div>
                                                        {order.confirmedAt && (
                                                            <div style={{ background: '#fff8e1', padding: '6px 12px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                <span>📦</span>
                                                                <span><strong>Confirmed:</strong> {new Date(order.confirmedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                                                            </div>
                                                        )}
                                                        {order.deliveredAt && (
                                                            <div style={{ background: '#e6f4ea', padding: '6px 12px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                <span>✅</span>
                                                                <span><strong>Delivered:</strong> {new Date(order.deliveredAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Full Address Section */}
                                                    <div style={{ background: '#fcf8f8', padding: '12px', borderRadius: '10px', marginBottom: '16px', fontSize: '0.9rem' }}>
                                                        <div style={{ fontWeight: '700', marginBottom: '4px', fontSize: '0.95rem' }}>
                                                            {order.user?.name}
                                                        </div>
                                                        <div style={{ color: '#555', lineHeight: '1.6' }}>
                                                            {addr.street && <>{addr.street}<br /></>}
                                                            {addr.city}{addr.state ? `, ${addr.state}` : ''}
                                                            {addr.zipCode ? ` — ${addr.zipCode}` : ''}
                                                            {addr.country && addr.country !== 'India' ? <><br />{addr.country}</> : ''}
                                                        </div>
                                                        {order.user?.email && (
                                                            <div style={{ color: 'var(--text-light)', fontSize: '0.82rem', marginTop: '6px' }}>✉ {order.user.email}</div>
                                                        )}
                                                    </div>

                                                    {order.deliveryStatus !== 'Delivered' && (
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '12px', border: '1px solid var(--border)', borderRadius: '10px', background: '#fafafa' }}>
                                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                                    <label style={{ fontSize: '0.8rem', fontWeight: '600', color: 'var(--text-light)' }}>
                                                                        📦 Tracking ID
                                                                    </label>
                                                                    <input
                                                                        type="text"
                                                                        placeholder="e.g. EE123456789IN"
                                                                        value={trackingInputs[orderId] !== undefined ? trackingInputs[orderId] : (order.trackingId || '')}
                                                                        onChange={(e) => handleTrackingInputChange(orderId, e.target.value)}
                                                                        style={{
                                                                            padding: '9px 12px', border: '1px solid var(--border)', borderRadius: '8px',
                                                                            width: '100%', fontFamily: 'inherit', fontSize: '0.9rem',
                                                                            background: 'white', boxSizing: 'border-box'
                                                                        }}
                                                                    />
                                                                </div>
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                                    <label style={{ fontSize: '0.8rem', fontWeight: '600', color: 'var(--text-light)' }}>
                                                                        🏤 Post Office
                                                                    </label>
                                                                    <input
                                                                        type="text"
                                                                        placeholder="e.g. GPO New Delhi"
                                                                        value={postOfficeInputs[orderId] !== undefined ? postOfficeInputs[orderId] : (order.postOffice || '')}
                                                                        onChange={(e) => handlePostOfficeInputChange(orderId, e.target.value)}
                                                                        style={{
                                                                            padding: '9px 12px', border: '1px solid var(--border)', borderRadius: '8px',
                                                                            width: '100%', fontFamily: 'inherit', fontSize: '0.9rem',
                                                                            background: 'white', boxSizing: 'border-box'
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                                                {(!order.deliveryStatus || order.deliveryStatus === 'New') && (
                                                                    <button
                                                                        onClick={() => handleUpdateStatus(orderId, 'In Progress')}
                                                                        className="btn btn-forest"
                                                                        style={{ padding: '8px 16px', fontSize: '0.85rem', flex: 1, opacity: (trackingInputs[orderId] || order.trackingId) ? 1 : 0.5 }}
                                                                        disabled={!(trackingInputs[orderId] || order.trackingId)}
                                                                        title={!(trackingInputs[orderId] || order.trackingId) ? 'Enter a tracking ID to confirm' : ''}
                                                                    >
                                                                        ✓ Confirm Order
                                                                    </button>
                                                                )}
                                                                {order.deliveryStatus === 'In Progress' && (
                                                                    <button
                                                                        onClick={() => handleUpdateStatus(orderId, 'Delivered')}
                                                                        className="btn btn-forest"
                                                                        style={{ padding: '8px 16px', fontSize: '0.85rem', flex: 1, backgroundColor: '#28a745' }}
                                                                    >
                                                                        ✓ Mark Delivered
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {order.deliveryStatus === 'Delivered' && (order.trackingId || order.postOffice) && (
                                                        <div style={{ fontSize: '0.85rem', margin: '8px 0 0 0', color: 'var(--text-light)', display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                                                            {order.trackingId && <span>📦 <strong>Tracking:</strong> {order.trackingId}</span>}
                                                            {order.postOffice && <span>🏤 <strong>Post Office:</strong> {order.postOffice}</span>}
                                                        </div>
                                                    )}
                                                    {order.deliveryStatus === 'In Progress' && (order.trackingId || order.postOffice) && !trackingInputs[orderId] && !postOfficeInputs[orderId] && (
                                                        <div style={{ fontSize: '0.85rem', margin: '0', color: 'var(--text-light)', display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                                                            {order.trackingId && <span>📦 <strong>Tracking:</strong> {order.trackingId}</span>}
                                                            {order.postOffice && <span>🏤 <strong>Post Office:</strong> {order.postOffice}</span>}
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="order-items-col" style={{ flex: 1, borderLeft: '1px solid var(--border)', paddingLeft: '24px' }}>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                                        {order.orderItems.map(item => (
                                                            <div key={item.book} style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                                                <div style={{ width: 40, height: 56, background: '#f0f0f0', borderRadius: 4, overflow: 'hidden', flexShrink: 0 }}>
                                                                    {item.image && <img src={item.image} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                                                                </div>
                                                                <div style={{ fontSize: '0.85rem' }}>
                                                                    <div style={{ fontWeight: '600' }}>{shorten(item.title, 25)}</div>
                                                                    <div style={{ color: 'var(--text-light)' }}>Qty: {item.qty}</div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', fontWeight: '700' }}>
                                                        <span>Total:</span>
                                                        <span style={{ color: 'var(--forest)' }}>₹{order.totalPrice}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* Delete Confirmation Modal */}
                {deletingBookId && (() => {
                    const bookToDelete = books.find(b => b._id === deletingBookId);
                    return (
                        <div className="delete-modal-overlay" onClick={() => setDeletingBookId(null)}>
                            <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
                                <div className="delete-modal-icon">🗑️</div>
                                <h3>Delete Book?</h3>
                                <p>
                                    You are about to permanently delete<br />
                                    <span className="book-title-highlight">
                                        {bookToDelete ? bookToDelete.title : 'this book'}
                                    </span>
                                </p>
                                <p style={{ fontSize: '0.8rem', color: '#dc3545', fontWeight: 600, marginTop: '8px' }}>
                                    ⚠️ This action cannot be undone.
                                </p>
                                <div className="delete-modal-actions">
                                    <button className="delete-modal-cancel" onClick={() => setDeletingBookId(null)}>
                                        Cancel
                                    </button>
                                    <button className="delete-modal-confirm" onClick={() => handleDeleteBook(deletingBookId)}>
                                        Yes, Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })()}

                {/* Order Detail Modal */}
                {detailOrderId && (() => {
                    const order = orders.find(o => String(o._id || o.id) === detailOrderId);
                    if (!order) return null;
                    const addr = order.shippingAddress || {};
                    const orderId = String(order._id || order.id || "");
                    const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
                    const statusColor = order.deliveryStatus === 'Delivered' ? '#1e7e34' : order.deliveryStatus === 'In Progress' ? '#f57f17' : '#666';
                    const statusBg = order.deliveryStatus === 'Delivered' ? '#e6f4ea' : order.deliveryStatus === 'In Progress' ? '#fff8e1' : '#f5f5f5';

                    return (
                        <div className="delete-modal-overlay" onClick={() => setDetailOrderId(null)} style={{ zIndex: 10000 }}>
                            <div onClick={(e) => e.stopPropagation()} style={{
                                background: 'white', borderRadius: '20px', padding: '0', maxWidth: '600px', width: '100%',
                                maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
                                boxShadow: '0 25px 80px rgba(0,0,0,0.25)', animation: 'slideUp 0.25s ease'
                            }}>
                                {/* Modal Header */}
                                <div style={{
                                    padding: '20px 24px', borderBottom: '1px solid var(--border)',
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)', flexShrink: 0
                                }}>
                                    <div>
                                        <h3 style={{ margin: '0 0 4px 0', fontSize: '1.15rem', color: '#1a1a1a' }}>Order #{orderId.slice(-6)}</h3>
                                        <span style={{ fontSize: '0.78rem', color: '#999', fontFamily: 'monospace' }}>ID: {orderId}</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <span style={{
                                            padding: '5px 14px', borderRadius: '20px', fontSize: '0.78rem', fontWeight: '700',
                                            backgroundColor: statusBg, color: statusColor
                                        }}>
                                            {order.deliveryStatus || 'Pending'}
                                        </span>
                                        <button onClick={() => setDetailOrderId(null)} style={{
                                            border: 'none', background: 'none', fontSize: '1.4rem', cursor: 'pointer',
                                            color: '#999', padding: '4px', lineHeight: 1
                                        }}>×</button>
                                    </div>
                                </div>

                                {/* Modal Body — Scrollable */}
                                <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>

                                    {/* Timeline Dates */}
                                    <div style={{ marginBottom: '20px' }}>
                                        <h4 style={{ margin: '0 0 10px 0', fontSize: '0.85rem', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Timeline</h4>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.88rem' }}>
                                                <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#4285f4', flexShrink: 0 }}></span>
                                                <span style={{ color: '#555' }}><strong>Order Received:</strong> {fmtDate(order.createdAt)}</span>
                                            </div>
                                            {order.isPaid && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.88rem' }}>
                                                    <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#34a853', flexShrink: 0 }}></span>
                                                    <span style={{ color: '#555' }}><strong>Payment Received:</strong> {fmtDate(order.paidAt)}</span>
                                                </div>
                                            )}
                                            {order.confirmedAt && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.88rem' }}>
                                                    <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#fbbc04', flexShrink: 0 }}></span>
                                                    <span style={{ color: '#555' }}><strong>Confirmed (Tracking Set):</strong> {fmtDate(order.confirmedAt)}</span>
                                                </div>
                                            )}
                                            {order.deliveredAt && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.88rem' }}>
                                                    <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#1e7e34', flexShrink: 0 }}></span>
                                                    <span style={{ color: '#555' }}><strong>Delivered:</strong> {fmtDate(order.deliveredAt)}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Customer & Address */}
                                    <div style={{ marginBottom: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                        <div>
                                            <h4 style={{ margin: '0 0 8px 0', fontSize: '0.85rem', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Customer</h4>
                                            <div style={{ background: '#f8f9fa', padding: '12px', borderRadius: '10px', fontSize: '0.88rem', lineHeight: 1.7 }}>
                                                <div style={{ fontWeight: 700 }}>{order.user?.name || '—'}</div>
                                                {order.user?.email && <div style={{ color: '#666' }}>✉ {order.user.email}</div>}
                                            </div>
                                        </div>
                                        <div>
                                            <h4 style={{ margin: '0 0 8px 0', fontSize: '0.85rem', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Shipping Address</h4>
                                            <div style={{ background: '#f8f9fa', padding: '12px', borderRadius: '10px', fontSize: '0.88rem', lineHeight: 1.7 }}>
                                                {addr.street && <div>{addr.street}</div>}
                                                <div>{addr.city}{addr.state ? `, ${addr.state}` : ''}{addr.zipCode ? ` — ${addr.zipCode}` : ''}</div>
                                                <div style={{ color: '#888' }}>{addr.country || 'India'}</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Payment & Tracking */}
                                    <div style={{ marginBottom: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                        <div>
                                            <h4 style={{ margin: '0 0 8px 0', fontSize: '0.85rem', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Payment</h4>
                                            <div style={{ background: '#f8f9fa', padding: '12px', borderRadius: '10px', fontSize: '0.88rem', lineHeight: 1.7 }}>
                                                <div><strong>Method:</strong> {order.paymentMethod || '—'}</div>
                                                <div><strong>Status:</strong> <span style={{ color: order.isPaid ? '#1e7e34' : '#dc3545', fontWeight: 600 }}>{order.isPaid ? 'Paid' : 'Unpaid'}</span></div>
                                                {order.paymentResult?.id && <div style={{ color: '#888', fontSize: '0.8rem' }}>Ref: {order.paymentResult.id}</div>}
                                            </div>
                                        </div>
                                        <div>
                                            <h4 style={{ margin: '0 0 8px 0', fontSize: '0.85rem', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Shipping Info</h4>
                                            <div style={{ background: '#f8f9fa', padding: '12px', borderRadius: '10px', fontSize: '0.88rem', lineHeight: 1.7 }}>
                                                <div><strong>Tracking:</strong> {order.trackingId || '—'}</div>
                                                <div><strong>Post Office:</strong> {order.postOffice || '—'}</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Coupon */}
                                    {order.coupon && (
                                        <div style={{ marginBottom: '20px', background: '#f0faf0', padding: '10px 14px', borderRadius: '10px', fontSize: '0.88rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span>🏷️ Coupon: <strong>{order.coupon.code}</strong></span>
                                            <span style={{ color: '#1e7e34', fontWeight: 700 }}>−₹{order.discountAmount}</span>
                                        </div>
                                    )}

                                    {/* Order Items */}
                                    <div>
                                        <h4 style={{ margin: '0 0 10px 0', fontSize: '0.85rem', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Items ({order.orderItems.length})</h4>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                            {order.orderItems.map((item, idx) => (
                                                <div key={idx} style={{ display: 'flex', gap: '12px', alignItems: 'center', padding: '10px', background: '#fafafa', borderRadius: '10px' }}>
                                                    <div style={{ width: 48, height: 64, background: '#eee', borderRadius: 6, overflow: 'hidden', flexShrink: 0 }}>
                                                        {item.image && <img src={item.image} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                                                    </div>
                                                    <div style={{ flex: 1, fontSize: '0.88rem' }}>
                                                        <div style={{ fontWeight: 600 }}>{item.title}</div>
                                                        <div style={{ color: '#888', fontSize: '0.8rem' }}>Qty: {item.qty}</div>
                                                    </div>
                                                    <div style={{ fontWeight: 700, fontSize: '0.95rem', whiteSpace: 'nowrap' }}>₹{(item.price * item.qty).toFixed(0)}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Modal Footer — Price Breakdown */}
                                <div style={{
                                    padding: '16px 24px', borderTop: '1px solid var(--border)',
                                    background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)', flexShrink: 0
                                }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.88rem' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', color: '#666' }}>
                                            <span>Items Subtotal</span>
                                            <span>₹{order.itemsPrice}</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', color: '#666' }}>
                                            <span>Shipping</span>
                                            <span>{order.shippingPrice > 0 ? `₹${order.shippingPrice}` : 'Free'}</span>
                                        </div>
                                        {order.discountAmount > 0 && (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', color: '#1e7e34' }}>
                                                <span>Discount</span>
                                                <span>−₹{order.discountAmount}</span>
                                            </div>
                                        )}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '1.05rem', paddingTop: '8px', borderTop: '1px solid var(--border)', marginTop: '4px' }}>
                                            <span>Total</span>
                                            <span style={{ color: 'var(--forest)' }}>₹{order.totalPrice}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })()}
            </div>
        </div>
    );
};

export default AdminPanel;
